import { Component, OnDestroy, OnInit } from '@angular/core'
import { EmployerNavigationService } from '@employer/app/services/navigation.service'
import { AssistantGateService } from '../../services/assistant-gate.service'

/**
 * Terminal view — an A/B alternate presentation of the SAME resizable panel (ux-command-bar-spec.md). It
 * is self-contained: the conversation fills the panel full-width with the composer full-width at its
 * bottom (Claude-Code-terminal style), instead of the dock (stream only) + a separate command bar. Drag
 * the top grip to resize, exactly like the dock. Tier-gated like the dock/bar (entitled → live chat; else
 * the Cnect Plus upsell), and it shares the same stream + composer + backend seam as the bar view.
 */
@Component({
  selector: 'assistant-terminal',
  template: `
    <div
      class="e11-flex e11-flex-col e11-bg-skin-white e11-border e11-border-skin-app-borders e11-rounded-lg e11-shadow-xl e11-overflow-hidden"
      [style.height.px]="height"
      [ngSwitch]="gate$ | async"
    >
      <!-- Resize grip -->
      <div
        class="e11-flex e11-items-center e11-justify-center e11-h-4 e11-cursor-ns-resize e11-shrink-0"
        (pointerdown)="startResize($event)"
        [attr.aria-label]="'Resize' | translate"
        role="separator"
      >
        <span class="e11-w-8 e11-h-1 e11-rounded-full e11-bg-skin-app-borders"></span>
      </div>

      <!-- Header: title + close -->
      <div class="e11-flex e11-items-center e11-gap-2 e11-px-4 e11-pb-2 e11-shrink-0">
        <span class="material-icons-outlined md-24 e11-text-skin-primary-accent">auto_awesome</span>
        <h3 class="e11-mb-0 e11-flex-1 e11-font-primary-demibold e11-text-skin-base">{{ 'Assistant' | translate }}</h3>
        <e11-button
          [color]="'secondary'"
          [ghost]="true"
          symbol="close"
          [value]="''"
          [titleText]="'Close' | translate"
          [attr.aria-label]="'Close' | translate"
          (click)="close()"
        ></e11-button>
      </div>

      <!-- Entitled: transcript (fills) + full-width composer pinned at the bottom. The guided walkthrough
           is its own floating surface (<tour-card>); this panel shows only the real conversation. The
           composer stays live below so a typed question reaches the real agent. -->
      <ng-container *ngSwitchCase="'entitled'">
        <div class="e11-flex-1 e11-min-h-0 e11-px-4">
          <assistant-chat-panel class="e11-h-full e11-block"></assistant-chat-panel>
        </div>
        <div class="e11-shrink-0 e11-px-4 e11-pb-4 e11-pt-2">
          <assistant-bar-active [fullWidth]="true"></assistant-bar-active>
        </div>
      </ng-container>

      <!-- Default / error → upsell -->
      <upgrade-prompt
        *ngSwitchCase="'upsell'"
        class="e11-px-4 e11-pb-4"
        [variant]="'inline-banner'"
        [featureName]="'Cnect Assistant'"
        [headline]="'The Cnect Assistant is a Cnect Plus feature.'"
        [body]="'Upgrade to Cnect Plus to chat with the assistant and automate your hiring workflow.'"
      ></upgrade-prompt>

      <div *ngSwitchDefault class="e11-flex-1 e11-m-4 e11-animate-pulse e11-bg-slate-200 e11-rounded-md"></div>
    </div>
  `,
})
export class AssistantTerminalComponent implements OnInit, OnDestroy {
  readonly gate$ = this.gateService.gate$

  private readonly minHeight = 220
  /** Panel height in px; user-adjustable via the top grip. Defaults to roughly a third of the viewport
   *  (the page stays readable when the panel opens) and can be dragged up to near full-screen (leaving
   *  room for the header) or down to a short panel. */
  height = this.clampHeight(Math.round(this.viewportHeight() * 0.35))

  private resizeStartY = 0
  private resizeStartHeight = 0
  private resizing = false

  private viewportHeight(): number {
    return typeof window !== 'undefined' ? window.innerHeight : 900
  }

  /** Upper bound: nearly the whole viewport, leaving space for the app header above the panel. */
  private maxHeight(): number {
    return Math.max(this.minHeight, this.viewportHeight() - 120)
  }

  private clampHeight(value: number): number {
    return Math.max(this.minHeight, Math.min(this.maxHeight(), value))
  }

  private readonly onMove = (event: PointerEvent): void => {
    if (!this.resizing) return
    this.height = this.clampHeight(this.resizeStartHeight + (this.resizeStartY - event.clientY))
  }
  private readonly onUp = (): void => {
    this.resizing = false
  }

  constructor(private gateService: AssistantGateService, private navigationService: EmployerNavigationService) {}

  ngOnInit(): void {
    document.addEventListener('pointermove', this.onMove)
    document.addEventListener('pointerup', this.onUp)
  }

  ngOnDestroy(): void {
    document.removeEventListener('pointermove', this.onMove)
    document.removeEventListener('pointerup', this.onUp)
  }

  startResize(event: PointerEvent): void {
    this.resizing = true
    this.resizeStartY = event.clientY
    this.resizeStartHeight = this.height
    event.preventDefault()
  }

  close(): void {
    this.navigationService.setAssistantBarExpanded(false)
  }
}
